import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error" }); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        setAttempts(n);
        if (data.payment_status === "paid") { setStatus({ state: "paid", ...data }); return; }
        if (data.status === "expired") { setStatus({ state: "expired", ...data }); return; }
        if (n >= 8) { setStatus({ state: "timeout", ...data }); return; }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) { setStatus({ state: "error" }); }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="payment-return">
      <div className="bg-white border border-slate-200 rounded-sm p-12 max-w-xl w-full text-center shadow-sm">
        {status.state === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-[var(--brand-primary)] mx-auto animate-spin" />
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-5">Confirming payment</div>
            <p className="text-slate-600 mt-2 text-sm">Attempt {attempts + 1} of 8…</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-5">Subscription active</h2>
            <p className="text-slate-600 mt-2">Your plan is enabled. Welcome aboard.</p>
            <Link to="/dashboard">
              <Button className="mt-7 bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-medium px-6 py-5" data-testid="paid-dashboard-btn">
                Back to dashboard
              </Button>
            </Link>
          </>
        )}
        {(status.state === "expired" || status.state === "error" || status.state === "timeout") && (
          <>
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-5">
              {status.state === "expired" ? "Session expired" : status.state === "timeout" ? "Still processing" : "Could not verify"}
            </h2>
            <p className="text-slate-600 mt-2">Try again from the pricing page.</p>
            <Link to="/pricing">
              <Button className="mt-7 bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-medium px-6 py-5" data-testid="retry-pricing-btn">
                Back to pricing
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
